/**
 * Lucide icon registry + MDI legacy name fallback.
 *
 * Backed by the framework-agnostic `lucide` package (vanilla JS icon data,
 * zero Vue dependency — works on Vue 2.7 and Vue 3). Each icon is an
 * `IconNode = [tag, attrs][]`; UiIcon.vue renders it as an inline SVG string.
 *
 * Two layers:
 * 1. `lucideIcons` — explicit registry of the Lucide icons used in this app,
 *    keyed by Lucide kebab name. Call sites reference these directly:
 *    `<UiIcon name="plus" />`, `<UiIcon name="trash-2" />`.
 * 2. `mdiToLucide` — maps legacy Material Design Icons ligature names (snake_case)
 *    to their Lucide kebab equivalent, so icon values still sourced from
 *    app config / store data (`field.icon`, `page.icon`, `getIcons(...)` arrays)
 *    render without a data migration. Call sites that move to Lucide names
 *    bypass this table.
 *
 * To add an icon: import its PascalCase export from `lucide`, register it in
 * `lucideIcons` under the Lucide kebab name. If a legacy MDI name must keep
 *    working, add an entry to `mdiToLucide`.
 */
import {
  Ad,
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUp,
  AtSign,
  Ban,
  Bell,
  Bug,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CirclePlus,
  CircleX,
  Clock,
  CloudDownload,
  CloudUpload,
  Compass,
  Copy,
  Copyright,
  Crop,
  DatabaseBackup,
  Download,
  Droplet,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  Globe,
  GripVertical,
  History,
  type IconNode,
  Info,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LayoutPanelTop,
  Link,
  List,
  LocateFixed,
  Lock,
  LockOpen,
  Mail,
  Map,
  MapPin,
  Minimize2,
  Minus,
  Move3d,
  OctagonAlert,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Smartphone,
  Smile,
  Square,
  Timer,
  ToggleLeft,
  ToggleRight,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Undo,
  User,
  Users,
  Video,
  X,
} from 'lucide'

/**
 * Lucide SVG default attributes (mirrors `lucide/dist/esm/defaultAttributes`).
 * Inlined to avoid a deep import that could break under strict resolvers; the
 * package ships no `exports` map, so a deep ESM path is not guaranteed.
 */
const lucideDefaultAttributes: Record<string, string | number> = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 2,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
}

const lucideIcons: Record<string, IconNode | undefined> = {
  ad: Ad,
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-left-right': ArrowLeftRight,
  'arrow-up': ArrowUp,
  'at-sign': AtSign,
  bell: Bell,
  ban: Ban,
  bug: Bug,
  calendar: Calendar,
  check: Check,
  'circle-check': CircleCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'circle-plus': CirclePlus,
  'circle-x': CircleX,
  'cloud-download': CloudDownload,
  'cloud-upload': CloudUpload,
  compass: Compass,
  copy: Copy,
  crop: Crop,
  clock: Clock,
  'database-backup': DatabaseBackup,
  download: Download,
  droplet: Droplet,
  'ellipsis-vertical': EllipsisVertical,
  'external-link': ExternalLink,
  eye: Eye,
  'eye-off': EyeOff,
  file: File,
  globe: Globe,
  'grip-vertical': GripVertical,
  copyright: Copyright,
  history: History,
  layers: Layers,
  info: Info,
  'key-round': KeyRound,
  'layout-dashboard': LayoutDashboard,
  'layout-grid': LayoutGrid,
  'layout-panel-top': LayoutPanelTop,
  link: Link,
  list: List,
  'locate-fixed': LocateFixed,
  lock: Lock,
  'lock-open': LockOpen,
  'map-pin': MapPin,
  mail: Mail,
  map: Map,
  'minimize-2': Minimize2,
  minus: Minus,
  'move-3d': Move3d,
  'octagon-alert': OctagonAlert,
  pencil: Pencil,
  phone: Phone,
  plus: Plus,
  'refresh-cw': RefreshCw,
  search: Search,
  settings: Settings,
  smartphone: Smartphone,
  smile: Smile,
  timer: Timer,
  undo: Undo,
  'toggle-left': ToggleLeft,
  square: Square,
  'toggle-right': ToggleRight,
  'trash-2': Trash2,
  'trending-up': TrendingUp,
  'triangle-alert': TriangleAlert,
  user: User,
  users: Users,
  video: Video,
  x: X,
}

/**
 * Legacy Material Design Icons ligature names → Lucide kebab names.
 * Covers icon values still sourced from store/config data (snake_case strings
 * authored before the Lucide migration). Purely a render-time aliasing layer;
 * new call sites should pass the Lucide kebab name directly.
 */
const mdiToLucide: Record<string, string> = {
  add: 'plus',
  add_circle: 'circle-plus',
  apps: 'layout-grid',
  arrow_back: 'arrow-left',
  arrow_downward: 'arrow-down',
  arrow_upward: 'arrow-up',
  autorenew: 'refresh-cw',
  backup: 'database-backup',
  block: 'ban',
  bug_report: 'bug',
  calendar_today: 'calendar',
  copyright: 'copyright',
  check: 'check',
  check_circle: 'circle-check',
  chevron_left: 'chevron-left',
  chevron_right: 'chevron-right',
  close: 'x',
  cloud_download: 'cloud-download',
  cloud_upload: 'cloud-upload',
  compare_arrows: 'arrow-left-right',
  compress: 'minimize-2',
  content_copy: 'copy',
  crop: 'crop',
  crop_rotate: 'crop',
  crop_square: 'square',
  dashboard: 'layout-dashboard',
  delete: 'trash-2',
  drag_indicator: 'grip-vertical',
  edit: 'pencil',
  error: 'octagon-alert',
  expand_less: 'chevron-up',
  expand_more: 'chevron-down',
  explore: 'compass',
  event: 'calendar',
  email: 'mail',
  face: 'smile',
  insert_drive_file: 'file',
  file_download: 'download',
  grid_on: 'layout-grid',
  group: 'users',
  info: 'info',
  layers: 'layers',
  launch: 'external-link',
  list: 'list',
  link: 'link',
  list_alt: 'list',
  lock: 'lock',
  lock_open: 'lock-open',
  lock_outline: 'lock',
  map: 'map',
  person: 'user',
  phone: 'phone',
  more_vert: 'ellipsis-vertical',
  my_location: 'locate-fixed',
  public: 'globe',
  notifications: 'bell',
  opacity: 'droplet',
  place: 'map-pin',
  refresh: 'refresh-cw',
  remove: 'minus',
  restore: 'history',
  room: 'map-pin',
  search: 'search',
  settings: 'settings',
  smartphone: 'smartphone',
  sync: 'refresh-cw',
  timer: 'timer',
  schedule: 'clock',
  toggle_off: 'toggle-left',
  toggle_on: 'toggle-right',
  transform: 'move-3d',
  trending_up: 'trending-up',
  videocam: 'video',
  view_quilt: 'layout-panel-top',
  visibility: 'eye',
  visibility_off: 'eye-off',
  vpn_key: 'key-round',
  warning: 'triangle-alert',
  web: 'globe',
}

/**
 * Resolve an icon name to a Lucide `IconNode`, or `null` if unknown.
 *
 * Resolution order:
 *   1. Font Awesome names (`fa-*`, `fa fa-*`, `fa-solid fa-*`) → not Lucide;
 *      callers must route those to the FA backend.
 *   2. Exact Lucide kebab name in the registry.
 *   3. Legacy MDI ligature name via `mdiToLucide`.
 */
export function resolveLucideIcon(name: string): IconNode | null {
  const key = name.trim()
  if (key.startsWith('fa-') || key.startsWith('fa ')) return null
  if (lucideIcons[key]) return lucideIcons[key]
  const mapped = mdiToLucide[key]
  if (mapped && lucideIcons[mapped]) return lucideIcons[mapped]
  return null
}

/**
 * Build an inline SVG string for a Lucide `IconNode`.
 * Used by UiIcon.vue via `v-html` (framework-agnostic: Vue 2.7 and Vue 3).
 */
export function renderLucideSvg(
  iconNode: IconNode,
  sizePx: number,
  color: string | undefined,
): string {
  const attrs = {
    ...lucideDefaultAttributes,
    width: sizePx,
    height: sizePx,
    stroke: color ?? 'currentColor',
  }
  const attrStr = Object.entries(attrs)
    .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
    .join(' ')
  const inner = iconNode
    .map(([tag, a]) => {
      const s = Object.entries(a)
        .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
        .join(' ')
      return s ? `<${tag} ${s}/>` : `<${tag}/>`
    })
    .join('')
  return `<svg ${attrStr}>${inner}</svg>`
}
